/**
 * CSV helpers. Text cells that start with a formula character are prefixed with an apostrophe,
 * so a spreadsheet program never executes content that users typed (CSV injection).
 */

export type CsvCell = string | number | boolean | null | undefined;

const FORMULA_START = /^[=+\-@\t\r]/;

export function sanitizeCsvText(value: string): string {
  return FORMULA_START.test(value) ? `'${value}` : value;
}

export function csvEscape(value: CsvCell, delimiter = ','): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  const s = sanitizeCsvText(value);
  if (s.includes(delimiter) || s.includes('"') || s.includes('\n') || s.includes('\r')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export interface CsvOptions {
  delimiter?: string;
  bom?: boolean;
  newline?: string;
}

export function toCsv(header: string[], rows: CsvCell[][], opts: CsvOptions = {}): string {
  const d = opts.delimiter ?? ',';
  const nl = opts.newline ?? '\r\n';
  const lines = [header.map((h) => csvEscape(h, d)).join(d)];
  for (const r of rows) lines.push(r.map((c) => csvEscape(c, d)).join(d));
  return (opts.bom === false ? '' : '﻿') + lines.join(nl) + nl;
}

/** Parses CSV text (RFC 4180 quoting). Delimiter 'auto' picks ; , or tab from the first line. */
export function parseCsv(text: string, delimiter: string | 'auto' = 'auto'): string[][] {
  let src = text.startsWith('﻿') ? text.slice(1) : text;
  src = src.replace(/\r\n?/g, '\n');
  let d = delimiter;
  if (d === 'auto') {
    const first = src.split('\n', 1)[0] ?? '';
    const counts = [';', ',', '\t'].map((c) => [c, first.split(c).length - 1] as const);
    counts.sort((a, b) => b[1] - a[1]);
    d = counts[0][1] > 0 ? counts[0][0] : ',';
  }
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          cell += '"';
          i++;
        } else inQuotes = false;
      } else cell += ch;
      continue;
    }
    if (ch === '"' && cell === '') inQuotes = true;
    else if (ch === d) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n') {
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim() !== ''));
}
